import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useModulosAccesibles } from '../hooks/usePermisosUsuario';
import * as LucideIcons from 'lucide-react';

const { ChefHat, ChevronLeft, ChevronRight, X } = LucideIcons;

interface SidebarProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
}

interface MenuItem {
  id: string;
  moduloNombre: string;
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  isMobile = false,
  isOpen = true,
  onToggle,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Obtener módulos accesibles por el usuario desde la base de datos
  const { data: modulosAccesibles = [], isLoading: loadingModulos } =
    useModulosAccesibles(user?.id || 0);

  // Mapeo de módulos del sistema a configuración de menú.
  // Orden = ciclo operativo (Receta → Ingrediente → Proveedor → Compra →
  // Lote → Minuta → Cliente → Armar Pedido → Gestión → Cocina → Mermas →
  // Administración). Rutas/iconos/permisos NO se tocan, solo el orden.
  const menuItems: MenuItem[] = useMemo(() => {
    return [
      {
        id: 'dashboard',
        moduloNombre: 'Dashboard',
        label: 'Dashboard',
        icon: LucideIcons.Home,
        path: '/dashboard',
      },
      {
        id: 'recetas',
        moduloNombre: 'Recetas',
        label: 'Recetas',
        icon: ChefHat,
        path: '/recetas',
      },
      {
        id: 'ingredientes',
        moduloNombre: 'Ingredientes',
        label: 'Ingredientes',
        icon: LucideIcons.Package,
        path: '/ingredientes',
      },
      {
        id: 'proveedores',
        moduloNombre: 'Proveedores',
        label: 'Proveedores',
        icon: LucideIcons.Building2,
        path: '/proveedores',
      },
      {
        id: 'compras',
        moduloNombre: 'Compras',
        label: 'Compras',
        icon: LucideIcons.ShoppingCart,
        path: '/compras',
      },
      {
        id: 'lotes',
        moduloNombre: 'Lotes',
        label: 'Lotes',
        icon: LucideIcons.Calendar,
        path: '/lotes',
      },
      {
        id: 'minutas',
        moduloNombre: 'Minutas',
        label: 'Minutas',
        icon: ChefHat,
        path: '/minutas-nuevas',
      },
      {
        id: 'clientes',
        moduloNombre: 'Clientes',
        label: 'Clientes',
        icon: LucideIcons.Users,
        path: '/clientes',
        // badge: 'Pendiente',
      },
      {
        id: 'armar_pedido',
        moduloNombre: 'Pedidos',
        label: 'Armar Pedido',
        icon: LucideIcons.Clipboard,
        path: '/armar-pedido',
      },
      {
        id: 'pedidos',
        moduloNombre: 'Pedidos',
        label: 'Gestión de Pedidos',
        icon: LucideIcons.ClipboardList,
        path: '/pedidos',
      },
      {
        id: 'cocina',
        moduloNombre: 'Cocina',
        label: 'Cocina',
        icon: LucideIcons.Utensils,
        path: '/cocina',
      },
      // Fase 6 (2026-07-01): pantalla de tandas segmentadas día/noche
      {
        id: 'cocina_tandas',
        moduloNombre: 'Cocina',
        label: 'Cocina · Tandas',
        icon: LucideIcons.LayoutGrid,
        path: '/cocina-tandas',
      },
      {
        id: 'despacho',
        moduloNombre: 'Cocina',
        label: 'Despacho',
        icon: LucideIcons.Truck,
        path: '/despacho',
      },
      // {
      //   id: 'reportes_compras',
      //   moduloNombre: 'Reportes',
      //   label: 'Reportes de Compras',
      //   icon: LucideIcons.TrendingUp,
      //   path: '/reportes-compras',
      //   badge: 'Pendiente',
      // },
      // {
      //   id: 'reportes',
      //   moduloNombre: 'Reportes',
      //   label: 'Reportes',
      //   icon: LucideIcons.BarChart3,
      //   path: '/reportes',
      //   badge: 'Pendiente',
      // },
      {
        id: 'mermas',
        moduloNombre: 'Mermas',
        label: 'Mermas',
        icon: LucideIcons.Trash2,
        path: '/mermas',
        // badge: 'Pendiente',
      },
      {
        id: 'administracion',
        moduloNombre: 'Administración',
        label: 'Administración',
        icon: LucideIcons.Settings,
        path: '/admin',
      },
    ];
  }, []);

  // Filtrar elementos del menú basado en los módulos accesibles desde la base de datos
  const filteredMenuItems = useMemo(() => {
    if (loadingModulos || !user) return [];

    // Si el usuario es administrador (rol base), mostrar todo el menú
    if (user.rol === 'administrador') return menuItems;

    // Rol cliente: solo el módulo Despacho (vista de "Mis Despachos").
    if (user.rol === 'cliente') {
      return menuItems.filter((item) => item.id === 'despacho');
    }

    // Filtrar por módulos accesibles desde la base de datos
    // El campo 'nombre' de la BD contiene valores como 'dashboard', 'recetas', etc.
    // que coinciden con el 'id' de los menuItems
    const modulosAccesiblesNombres = modulosAccesibles.map((m) => m.nombre);
    return menuItems.filter((item) =>
      modulosAccesiblesNombres.includes(item.id)
    );
  }, [menuItems, modulosAccesibles, loadingModulos, user]);

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile && onToggle) {
      onToggle();
    }
  };

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Sidebar para móvil (overlay)
  if (isMobile) {
    return (
      <>
        {/* Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onToggle}
          />
        )}

        {/* Sidebar móvil */}
        <div
          className={`
          fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 lg:hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="bg-blue-600 p-2 rounded-lg">
                <ChefHat className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-gray-900 dark:text-gray-100">
                Suminuta
              </span>
            </div>
            <button
              onClick={onToggle}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <nav className="p-4">
            <ul className="space-y-2">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleNavigate(item.path)}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors duration-200
                        ${
                          isActive
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </>
    );
  }

  // Sidebar para desktop
  return (
    <div
      className={`
      h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300
      ${isCollapsed ? 'w-16' : 'w-64'}
    `}
    >
      {/* Header del sidebar */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="bg-blue-600 p-2 rounded-lg">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-gray-900 dark:text-gray-100">
              Suminuta
            </span>
          </div>
        )}
        <button
          onClick={toggleCollapsed}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          )}
        </button>
      </div>

      {/* Navegación */}
      <nav className="p-4">
        <ul className="space-y-2">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <li key={item.id}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors duration-200
                    ${
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && (
                    <>
                      <span className="font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-yellow-600 dark:text-yellow-400 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
